import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import {
  Phone, Settings, Copy, Check, Zap, PhoneCall, Clock, ChevronDown,
  ChevronUp, RefreshCw, Shield, CalendarCheck, XCircle, Info, User, Building2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); toast.success('Copiato!'); setTimeout(() => setCopied(false), 2000); } catch { toast.error('Impossibile copiare'); }
  };
  return <button onClick={copy} className="p-1.5 hover:bg-slate-100 rounded transition-colors" data-testid="copy-btn">{copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-slate-400" />}</button>;
}

function formatDuration(sec) {
  if (!sec) return '0:00';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getCallOutcome(log) {
  if (log.appointment_created) return { label: 'Prenotato', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CalendarCheck, dot: 'bg-emerald-500' };
  if (log.appointment_cancelled) return { label: 'Disdetta', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: XCircle, dot: 'bg-amber-500' };
  const transcript = log.transcript || [];
  const hasInfo = transcript.some(m => m.role === 'user' && /informazion|orari|dove|indirizzo|costo/i.test(m.content || ''));
  if (hasInfo) return { label: 'Informazioni', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Info, dot: 'bg-blue-500' };
  return { label: 'Chiamata', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: PhoneCall, dot: 'bg-slate-400' };
}

function CallCard({ log, idx, expanded, onToggle }) {
  const outcome = getCallOutcome(log);
  const OutcomeIcon = outcome.icon;
  const hasTranscript = log.transcript?.length > 0;
  const date = log.started_at ? new Date(log.started_at) : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-sm transition-shadow" data-testid={`call-log-${idx}`}>
      <div className="p-4 cursor-pointer" onClick={onToggle}>
        <div className="flex items-start gap-3">
          {/* Status dot */}
          <div className={`w-3 h-3 rounded-full mt-1.5 shrink-0 ${outcome.dot}`} />

          {/* Main info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-800">{log.patient_name || 'Sconosciuto'}</span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${outcome.color}`}>
                <OutcomeIcon className="w-3 h-3" /> {outcome.label}
              </span>
            </div>

            <div className="flex items-center gap-4 mt-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                <Phone className="w-3 h-3" /> {log.caller_phone || '—'}
              </span>
              {log.clinic_name && (
                <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                  <Building2 className="w-3 h-3" /> {log.clinic_name}
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-xs font-mono text-slate-500">
                <Clock className="w-3 h-3" /> {formatDuration(log.duration_seconds)}
              </span>
              {date && (
                <span className="text-xs text-slate-400">
                  {date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })} {date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>

            {/* Quick preview of last exchange */}
            {!expanded && hasTranscript && (
              <p className="text-xs text-slate-400 mt-2 truncate">
                {log.transcript[log.transcript.length - 1]?.role === 'assistant' ? 'AI: ' : 'Paziente: '}
                {log.transcript[log.transcript.length - 1]?.content?.slice(0, 80)}...
              </p>
            )}
          </div>

          {/* Expand arrow */}
          {hasTranscript && (
            <div className="shrink-0 p-1">
              {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </div>
          )}
        </div>
      </div>

      {/* Expanded transcript */}
      {expanded && hasTranscript && (
        <div className="border-t border-slate-100 bg-slate-50/70 p-4 max-h-72 overflow-y-auto space-y-2" data-testid={`transcript-${idx}`}>
          {log.transcript.map((msg, mi) => (
            <div key={mi} className={`flex ${msg.role === 'assistant' ? 'justify-start' : msg.role === 'system' ? 'justify-center' : 'justify-end'}`}>
              {msg.role === 'system' ? (
                <span className="text-[10px] text-red-400 bg-red-50 px-2 py-1 rounded">{msg.content}</span>
              ) : (
                <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'assistant'
                    ? 'bg-sky-100 text-sky-900 rounded-bl-sm'
                    : 'bg-white border border-slate-200 text-slate-800 rounded-br-sm'
                }`}>
                  <p className="text-[10px] font-semibold mb-0.5 opacity-50">
                    {msg.role === 'assistant' ? 'AI' : 'Paziente'}
                  </p>
                  {msg.content}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function VoiceSystem() {
  const { token, user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ telnyx_api_key: '', is_active: false });
  // Note: field name "telnyx_api_key" is kept for backend compatibility
  const [callLogs, setCallLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/voice/config`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setConfig(data);
      setFormData({ telnyx_api_key: data.config?.telnyx_api_key || '', is_active: data.config?.is_active || false });
    } catch {}
    finally { setLoading(false); }
  }, [token]);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await fetch(`${API}/api/voice/call-logs`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setCallLogs(data.logs || []);
    } catch {}
    finally { setLogsLoading(false); }
  }, [token]);

  useEffect(() => { fetchConfig(); fetchLogs(); }, [fetchConfig, fetchLogs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/voice/config`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      data.success ? toast.success('Salvato!') : toast.error(data.error || 'Errore');
    } catch { toast.error('Errore di rete'); }
    finally { setSaving(false); }
  };

  // Stats
  const totalCalls = callLogs.length;
  const booked = callLogs.filter(l => l.appointment_created).length;
  const cancelled = callLogs.filter(l => l.appointment_cancelled).length;
  const avgDuration = totalCalls > 0 ? Math.round(callLogs.reduce((sum, l) => sum + (l.duration_seconds || 0), 0) / totalCalls) : 0;

  if (loading) return (
    <Layout><div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" /></div></Layout>
  );

  return (
    <Layout>
      <div className="p-4 sm:p-8 max-w-5xl overflow-hidden" data-testid="voice-system-page">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Phone className="w-6 h-6 text-sky-500" /> TelcaVoIP Voice AI
          </h1>
          <p className="text-sm text-slate-500 mt-1">Sistema di risposta automatica AI per le chiamate</p>
        </div>

        {/* How it works */}
        <div className="bg-gradient-to-r from-sky-50 to-indigo-50 rounded-xl border border-sky-200 p-5 mb-6 overflow-hidden">
          <h3 className="font-semibold text-sky-800 text-sm mb-3 flex items-center gap-2"><Shield className="w-4 h-4" /> Come funziona</h3>
          <div className="flex flex-col sm:flex-row items-center gap-2 text-sm text-sky-700">
            <span className="px-3 py-1.5 bg-white rounded-lg border border-sky-200 text-xs sm:text-sm w-full sm:w-auto text-center">Paziente chiama</span>
            <span className="text-sky-400 hidden sm:inline">&#x2192;</span>
            <span className="text-sky-400 sm:hidden">&#x2193;</span>
            <span className="px-3 py-1.5 bg-sky-600 text-white rounded-lg font-medium text-xs sm:text-sm w-full sm:w-auto text-center">TelcaVoIP AI risponde</span>
            <span className="text-sky-400 hidden sm:inline">&#x2192;</span>
            <span className="text-sky-400 sm:hidden">&#x2193;</span>
            <span className="px-3 py-1.5 bg-white rounded-lg border border-sky-200 text-xs sm:text-sm w-full sm:w-auto text-center">Prenota appuntamento</span>
          </div>
        </div>

        {/* Status + Mini Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          <div className="col-span-2 sm:col-span-3 lg:col-span-1 p-3 sm:p-4 rounded-xl border bg-emerald-50 border-emerald-200 flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse shrink-0" />
            <span className="font-semibold text-emerald-800 text-sm">Attivo</span>
          </div>
          <div className="p-3 sm:p-4 rounded-xl border border-slate-200 bg-white text-center">
            <p className="text-xl sm:text-2xl font-bold text-slate-800">{totalCalls}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Chiamate</p>
          </div>
          <div className="p-3 sm:p-4 rounded-xl border border-slate-200 bg-white text-center">
            <p className="text-xl sm:text-2xl font-bold text-emerald-600">{booked}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Prenotate</p>
          </div>
          <div className="p-3 sm:p-4 rounded-xl border border-slate-200 bg-white text-center">
            <p className="text-xl sm:text-2xl font-bold text-amber-600">{cancelled}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Disdette</p>
          </div>
          <div className="p-3 sm:p-4 rounded-xl border border-slate-200 bg-white text-center">
            <p className="text-xl sm:text-2xl font-bold text-slate-800">{formatDuration(avgDuration)}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Media</p>
          </div>
        </div>

        {/* Superadmin Config */}
        {isSuperadmin && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 space-y-5 mb-6" data-testid="voice-config-form">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
              <Settings className="w-5 h-5 text-sky-600 shrink-0" /> Configurazione Tecnica
              <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full">Solo Superadmin</span>
            </h2>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key Telefonia</label>
              <input type="password" value={formData.telnyx_api_key} onChange={(e) => setFormData({ ...formData, telnyx_api_key: e.target.value })}
                placeholder="KEY..." className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-sm" data-testid="voice-api-key-input" />
            </div>
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="sr-only peer" data-testid="voice-active-toggle" />
                <div className="w-11 h-6 bg-slate-200 peer-focus:ring-4 peer-focus:ring-sky-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600"></div>
              </label>
              <span className="text-sm font-medium text-slate-700">Sistema Attivo</span>
            </div>
            <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors font-medium text-sm disabled:opacity-50" data-testid="save-voice-config-btn">
              {saving ? 'Salvataggio...' : 'Salva Configurazione'}
            </button>

            {config?.endpoints && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <h3 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2"><Zap className="w-4 h-4" /> Endpoint Webhook</h3>
                <div className="space-y-2">
                  {Object.entries(config.endpoints).map(([name, url]) => (
                    <div key={name} className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg overflow-hidden" data-testid={`endpoint-${name}`}>
                      <span className="px-2 py-0.5 text-xs font-mono font-bold bg-sky-100 text-sky-700 rounded shrink-0">{name.includes('websocket') ? 'WS' : 'POST'}</span>
                      <code className="flex-1 text-xs font-mono text-slate-600 truncate min-w-0">{url}</code>
                      <CopyBtn text={url} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Call Logs */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-sky-600" /> Registro Chiamate
              {totalCalls > 0 && <span className="px-2 py-0.5 bg-sky-100 text-sky-700 text-xs rounded-full">{totalCalls}</span>}
            </h2>
            <button onClick={fetchLogs} disabled={logsLoading} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="refresh-logs-btn">
              <RefreshCw className={`w-4 h-4 text-slate-500 ${logsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {callLogs.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <PhoneCall className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-500">Nessuna chiamata AI registrata</p>
              <p className="text-xs text-slate-400 mt-1">Le chiamate appariranno qui dopo l'attivazione</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="voice-call-logs">
              {callLogs.map((log, idx) => (
                <CallCard
                  key={idx} log={log} idx={idx}
                  expanded={expandedLog === idx}
                  onToggle={() => setExpandedLog(expandedLog === idx ? null : idx)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
